import { t } from '@lingui/core/macro'
import { UserInfo } from './user-info'
import { getCommentText, updateCellWithApprovedComment } from '../../helpers'
import { StatusWrapper } from '../status-wrapper'
import { getUsers } from '@global/api/liveblocks'
import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { RootState } from '@redux/store'
import type { QueuedResolutionStatus, List } from '../../types'
import type { HotTable } from '@handsontable/react'
import type { ThreadData } from '@liveblocks/client'

interface TableCoords {
  coords: {
    r: number
    c: number
  }
}

type ThreadWithTable = ThreadData & {
  table?: TableCoords
}

interface CommentProps {
  thread: ThreadWithTable
  updateLocalThreadStatus?: (threadId: string, status: QueuedResolutionStatus) => void
  queueThreadResolution?: (thread: ThreadData, status: QueuedResolutionStatus) => void
  tableRef: React.RefObject<HotTable>
  list: List
  isApproverMode: boolean
  inReviewMode: boolean
}

export const Comment = ({
  thread,
  updateLocalThreadStatus,
  queueThreadResolution,
  tableRef,
  list,
  isApproverMode,
  inReviewMode,
}: CommentProps) => {
  const { metadata, updatedAt, table } = thread
  const currentUser = useSelector((state: RootState) => state.Auth.user)

  const userId = currentUser?.uuid?.toString() || 'unknown id'
  const userName = currentUser?.name || 'unknown user'

  const userIds = useMemo((): string[] => {
    const ids = thread.comments?.map(comment => comment.userId) || []
    return Array.from(new Set(ids))
  }, [thread.comments])

  const { data: usersMap = {} } = useQuery({
    queryKey: ['liveblocks', 'users', userIds],
    queryFn: () => getUsers(userIds),
    select: users => Object.fromEntries(users.map(user => [user.uuid, user.name])),
    enabled: userIds.length > 0,
  })

  const formattedComments = useMemo(
    () =>
      thread.comments.map(comment => {
        const text =
          comment.body?.content
            ?.map(block => {
              if ('children' in block && Array.isArray(block.children)) {
                return block.children
                  .map(child =>
                    'text' in child && typeof child.text === 'string' ? child.text : '',
                  )
                  .join(' ')
              }
              return ''
            })
            .join(' ') || ''

        return {
          text,
          createdAt: comment.createdAt,
          userId: comment.userId,
          userName: usersMap[comment.userId] || t`Anonymous`,
        }
      }),
    [thread.comments, usersMap],
  )

  const handleStatusUpdate = (status: QueuedResolutionStatus) => {
    if (!updateLocalThreadStatus || !queueThreadResolution) return

    // Update local thread status immediately
    updateLocalThreadStatus(thread.id, status)

    const updatedThread = {
      ...thread,
      metadata: {
        ...thread.metadata,
        moderatedById: userId,
        moderatedByName: userName,
        moderatedAt: new Date().toISOString(),
        status,
      },
      resolved: true,
    }

    // Queue the resolution for when we save
    queueThreadResolution(updatedThread, status)

    if (status === 'approved' && tableRef?.current?.hotInstance && list) {
      updateCellWithApprovedComment(thread, tableRef, list)
    }
  }

  const statusTitle = useMemo(() => {
    if (metadata.type === 'general') {
      return t`General`
    }

    const rowNumber = table?.coords.r ?? -1
    const columnNumber = table?.coords.c ?? -1
    if (metadata.status === 'pending') {
      return table ? t`Row ${rowNumber}, Column ${columnNumber}` : t`General`
    }

    return t`Suggestion`
  }, [metadata.type, metadata.status])

  return (
    <StatusWrapper status={metadata.status}>
      <div className="flex-grow basis-0 shrink px-[15px] pr-[24px] py-[13px] max-w-[322px]">
        <div className="flex justify-between text-xs text-[#64748B]">
          <span>{statusTitle}</span>
          <span className="capitalize italic">{metadata.status}</span>
        </div>

        {metadata.type !== 'general' ? (
          <>
            <UserInfo
              inReviewMode={inReviewMode}
              userName={metadata.userName}
              actionTime={new Date(updatedAt)}
              isApproverMode={isApproverMode}
              isPending={metadata.status === 'pending'}
              onStatusUpdate={handleStatusUpdate}
            />
            <div className="text-sm text-cv-slate-800 space-y-1 leading-snug break-words whitespace-normal cursor-default">
              <p>
                {t`Original:`}{' '}
                {metadata.status === 'approved' ? metadata.prevValue : metadata.currentValue}
              </p>
              <p>
                {t`Suggestion:`} {getCommentText(thread)}
              </p>
            </div>
            {metadata.moderatedByName && (
              <div className="cursor-default">
                <UserInfo
                  inReviewMode={inReviewMode}
                  userName={metadata.moderatedByName}
                  actionTime={metadata.moderatedAt ? new Date(metadata.moderatedAt) : new Date()}
                  isModerator
                />
                <p className="italic">
                  {t`Marked as `} {metadata.status}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {formattedComments.map(comment => (
              <div
                key={comment.userId + String(comment.createdAt)}
                className="text-sm text-cv-slate-800 leading-none"
              >
                <UserInfo
                  inReviewMode={inReviewMode}
                  userName={comment.userName}
                  actionTime={new Date(comment.createdAt)}
                  isModerator
                />
                <p>{comment.text}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </StatusWrapper>
  )
}
